import { useQuery } from '@tanstack/react-query'
import { AlertCircle, AlertTriangle, ArrowLeft } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../api'
import { AudioPlayer } from '../components/AudioPlayer'
import { DispoBadge } from '../components/DispoBadge'
import {
  KeyboardShortcuts,
  ShortcutHint,
  type ShortcutMap,
} from '../components/KeyboardShortcuts'
import { METRIC_WEIGHTS, MetricBarRow, MetricCard } from '../components/MetricCard'
import { ReviewPanel } from '../components/ReviewPanel'
import { Transcript } from '../components/Transcript'
import type { PerMetricResult, ReviewMetric } from '../types'

// MetricBarRow strip below the cards keeps a stable order for at-a-glance
// pass/fail bars on the original 5 rubric metrics. Cards above are dynamic
// (every metric the API returns, sorted by weight DESC).
const BAR_ROW_ORDER = [
  'talk_ratio',
  'longest_monologue',
  'next_step_commitment',
  'objection_delta',
  'dispo_alignment',
] as const

function sortedMetricKeys(per_metric: Record<string, PerMetricResult>): string[] {
  return Object.keys(per_metric).sort((a, b) => {
    const wa = METRIC_WEIGHTS[a] ?? 0
    const wb = METRIC_WEIGHTS[b] ?? 0
    if (wb !== wa) return wb - wa
    return a.localeCompare(b)
  })
}

export function CallDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const callId = Number(id)

  const audioRef = useRef<HTMLAudioElement>(null)
  const [currentTime, setCurrentTime] = useState(0)

  const { data, isLoading, error } = useQuery({
    queryKey: ['call', callId],
    queryFn: () => api.calls.get(callId),
    enabled: Number.isFinite(callId),
  })

  // Keymap handlers hoisted out of the useMemo object so Biome's nursery
  // rule noNestedComponentDefinitions doesn't see PascalCase keys on arrow
  // functions and mistake them for nested React components.
  const handleSpace = useCallback(() => {
    const a = audioRef.current
    if (!a) return
    if (a.paused) void a.play()
    else a.pause()
  }, [])
  const handleReload = useCallback(() => window.location.reload(), [])
  const handleEscape = useCallback(() => navigate(-1), [navigate])

  const shortcuts: ShortcutMap = useMemo(
    () => ({ Space: handleSpace, r: handleReload, Escape: handleEscape }),
    [handleSpace, handleReload, handleEscape]
  )

  useEffect(() => {
    const a = audioRef.current
    if (!a) return
    const onTime = () => setCurrentTime(a.currentTime)
    a.addEventListener('timeupdate', onTime)
    return () => a.removeEventListener('timeupdate', onTime)
  }, [])

  const handleSeek = (sec: number) => {
    const a = audioRef.current
    if (!a) return
    a.currentTime = sec
    if (a.paused) void a.play()
  }

  if (!Number.isFinite(callId)) {
    return <p className="text-sm text-red-300">Bad call id.</p>
  }

  return (
    <div className="space-y-4">
      <KeyboardShortcuts shortcuts={shortcuts} />
      <header className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
        >
          <ArrowLeft className="h-3 w-3" /> Back
        </button>
        <h1 className="text-xl font-semibold">Call #{callId}</h1>
        <div className="ml-auto">
          <ShortcutHint
            keys={[
              ['␣', 'play / pause'],
              ['Esc', 'back'],
              ['R', 'refresh'],
            ]}
          />
        </div>
      </header>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-900 bg-red-950/40 p-3 text-sm text-red-200">
          <AlertCircle className="mt-0.5 h-4 w-4" />
          <span>{(error as Error).message}</span>
        </div>
      )}

      {isLoading || !data ? (
        <div className="space-y-3">
          <div className="h-20 animate-pulse rounded bg-slate-800/60" />
          <div className="h-80 animate-pulse rounded bg-slate-800/60" />
        </div>
      ) : (
        <>
          <DispoAlignmentHeader
            dispo={data.call.vici_disposition}
            result={(data.per_metric as Record<string, PerMetricResult>).dispo_alignment}
          />

          <section className="grid gap-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4 md:grid-cols-[1fr_auto]">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to={`/agents/${encodeURIComponent(data.call.vici_user)}`}
                  className="text-sm font-medium text-slate-100 hover:text-indigo-300"
                >
                  {data.call.vici_user}
                </Link>
                {data.call.agent_name && (
                  <span className="text-xs text-slate-500">{data.call.agent_name}</span>
                )}
                <DispoBadge dispo={data.call.vici_disposition} size="md" />
                {data.call.campaign && (
                  <span className="rounded border border-slate-700 bg-slate-900 px-1.5 py-0.5 text-[10px] font-mono text-slate-400">
                    {data.call.campaign}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500">
                {new Date(data.call.call_started_at).toLocaleString()} · {fmtLen(data.call.length_sec)} ·{' '}
                <span className="font-mono">{data.call.recording_filename}</span>
              </p>
              {data.summary && (
                <p className="mt-2 rounded border border-slate-800 bg-slate-950/50 p-3 text-sm leading-relaxed text-slate-200">
                  {data.summary}
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">Overall</span>
              <span className="text-3xl font-bold tabular-nums text-slate-100">
                {data.overall !== null && data.overall !== undefined
                  ? data.overall.toFixed(1)
                  : '--'}
                <span className="text-base text-slate-500">/10</span>
              </span>
              <span className="text-xs text-slate-500">
                {data.metrics_passed ?? 0}/5 metrics passed
              </span>
            </div>
          </section>

          <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
            <AudioPlayer callId={callId} ref={audioRef} />
          </section>

          <section className="grid gap-4 lg:grid-cols-[2fr_3fr]">
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-slate-200">Metrics</h2>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
                {sortedMetricKeys(
                  data.per_metric as Record<string, PerMetricResult>
                ).map((m) => {
                  const r = (data.per_metric as Record<string, PerMetricResult>)[m]
                  if (!r) return null
                  return <MetricCard key={m} metric={m} result={r} />
                })}
              </div>
              <div className="mt-3 rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                {BAR_ROW_ORDER.map((m) => {
                  const r = (data.per_metric as Record<string, PerMetricResult>)[m]
                  if (!r) return null
                  return <MetricBarRow key={m} metric={m} result={r} />
                })}
              </div>
              <DispoMismatchPanel
                dispo={data.call.vici_disposition}
                result={(data.per_metric as Record<string, PerMetricResult>).dispo_alignment}
              />
            </div>
            <div className="space-y-2">
              <h2 className="text-sm font-semibold text-slate-200">Transcript</h2>
              <Transcript
                segments={data.transcript?.segments ?? null}
                currentTime={currentTime}
                onSeek={handleSeek}
              />
            </div>
          </section>

          <ReviewPanel
            callId={callId}
            passedByMetric={buildPassedMap(
              data.per_metric as Record<string, PerMetricResult>,
              data.overall
            )}
            predictedDispo={extractPredictedDispo(
              (data.per_metric as Record<string, PerMetricResult>).dispo_alignment
            )}
          />
        </>
      )}
    </div>
  )
}

function buildPassedMap(
  per_metric: Record<string, PerMetricResult>,
  overall: number | null | undefined
): Partial<Record<ReviewMetric, boolean | null>> {
  const map: Partial<Record<ReviewMetric, boolean | null>> = {}
  for (const [k, v] of Object.entries(per_metric)) {
    map[k as ReviewMetric] = v?.passed ?? null
  }
  // Heuristic: overall passes if ≥ 5.0 (matches check_gates team avg floor).
  map.overall = overall != null ? overall >= 5.0 : null
  return map
}

function extractPredictedDispo(
  dispoResult: PerMetricResult | undefined
): string | null {
  if (!dispoResult) return null
  const details = dispoResult.details as { predicted?: string } | null
  return details?.predicted ?? null
}

function DispoAlignmentHeader({
  dispo,
  result,
}: {
  dispo: string
  result: PerMetricResult | undefined
}) {
  const details = (result?.details ?? null) as { predicted?: string } | null
  const predicted = details?.predicted ?? null
  const match = result?.passed ?? null
  const pending = !result
  const matchStyle =
    match === true
      ? 'border-emerald-800 bg-emerald-950/40'
      : match === false
        ? 'border-amber-800 bg-amber-950/40'
        : 'border-slate-800 bg-slate-900/40'
  return (
    <section
      className={`flex flex-wrap items-center gap-6 rounded-lg border p-4 ${matchStyle}`}
    >
      <div className="flex flex-col gap-1">
        <span className="text-[10px] uppercase tracking-wide text-slate-500">
          Dispo (agent)
        </span>
        <DispoBadge dispo={dispo} size="md" />
      </div>
      <span className="text-lg text-slate-500">vs.</span>
      <div className="flex flex-col gap-1">
        <span className="text-[10px] uppercase tracking-wide text-slate-500">
          AI Dispo (Qwen)
        </span>
        <DispoBadge dispo={predicted ?? '??'} size="md" />
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-[10px] uppercase tracking-wide text-slate-500">
          Match
        </span>
        <span
          className={`text-lg font-semibold ${
            match === true
              ? 'text-emerald-300'
              : match === false
                ? 'text-amber-300'
                : 'text-slate-400'
          }`}
        >
          {pending ? '…' : match ? '✓ yes' : '✗ no'}
        </span>
      </div>
      <p className="ml-auto max-w-md text-xs leading-relaxed text-slate-400">
        <strong className="text-slate-300">Dispo alignment</strong> compares the
        agent's VICIdial dispo against what Qwen predicts from the transcript
        alone. A mismatch means the rep probably clicked the wrong dispo button
        (or rushed / mis-categorized the call).
      </p>
    </section>
  )
}

function DispoMismatchPanel({
  dispo,
  result,
}: {
  dispo: string
  result: PerMetricResult | undefined
}) {
  if (!result) return null
  const details = result.details as { predicted?: string; rationale?: string } | null
  const predicted = details?.predicted ?? '??'
  const match = result.passed
  return (
    <div
      className={`rounded-lg border p-3 ${
        match ? 'border-slate-800 bg-slate-900/40' : 'border-amber-900 bg-amber-950/30'
      }`}
    >
      <div className="flex items-center gap-2">
        {match ? null : <AlertTriangle className="h-4 w-4 text-amber-400" />}
        <span className="text-sm font-medium text-slate-200">Dispo alignment</span>
      </div>
      <div className="mt-2 flex items-center gap-3 text-xs text-slate-300">
        <div>
          <span className="text-slate-500">VICIdial: </span>
          <DispoBadge dispo={dispo} />
        </div>
        <div className="text-slate-500">→</div>
        <div>
          <span className="text-slate-500">Predicted: </span>
          <DispoBadge dispo={predicted} />
        </div>
      </div>
      {details?.rationale && (
        <p className="mt-2 text-xs text-slate-400">{details.rationale}</p>
      )}
    </div>
  )
}

function fmtLen(sec: number): string {
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}
